import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * Button — Fastn Design System contract (fastn-design-system/docs/COMPONENTS.md).
 *
 * Doctrine points that differ from stock shadcn:
 * - Primary is the brightest monochrome pixel (offwhite on dark, ink on light).
 *   A coloured primary fill is an instant rejection; blue stays on links,
 *   focus rings and charts.
 * - Elevation is borders, not shadows — no shadow utilities anywhere.
 * - Destructive renders as an outline. A solid status-error fill is reserved
 *   for ConfirmDestructive, the one place the DS allows it.
 * - Control heights are 32 / 36 / 44, and md promotes to the 44px touch target
 *   on coarse pointers.
 */
const buttonVariants = cva(
  cn(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md",
    "text-sm font-medium leading-none transition-colors",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  ),
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary-hover",
        destructive:
          "border border-error/40 bg-transparent text-error hover:border-error hover:bg-error/10",
        outline:
          "border border-border-strong bg-transparent hover:bg-raised",
        secondary:
          "border border-border bg-raised text-fg hover:border-border-strong",
        ghost: "hover:bg-raised hover:text-fg",
        link: "text-brand underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 pointer-coarse:h-11",
        sm: "h-8 px-3 text-xs",
        lg: "h-11 px-6",
        icon: "h-9 w-9 pointer-coarse:size-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Swaps the label for `loadingLabel`, disables the control, sets aria-busy. */
  loading?: boolean;
  /** Present-progressive label shown while loading ("Saving…"). */
  loadingLabel?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      loadingLabel,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    // asChild forwards a single child untouched, so a label swap would break
    // the Slot contract — loading only applies to real buttons.
    const swap = !asChild && loading && loadingLabel !== undefined;
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={asChild ? undefined : disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {swap ? loadingLabel : children}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
